import { API_VERSION } from 'constants/common';
import { baseApi } from 'services/api';
import {
  BaseIntegrationsPayload,
  CreateDeveloperAPITokenPayload,
  CreateDeveloperAPITokenResponse,
  CreateWebhookUrlPayload,
  DeleteDeveloperAPITokenPayload,
  DeleteWebhookUrlPayload,
  DisconnectQBOResponse,
  EditQBOAccountPayload,
  GetDeveloperApiTokensResponse,
  GetExpenseAndRevenueAccountsRequest,
  GetIntegrationsResponse,
  GetWebhookUrlByIdPayload,
  GetWebhookUrlsResponse,
  QBOAccountsResponse,
  QBOConnectionDataResponse,
  QBOConnectionStatusResponse,
  QBOLoginURLResponse,
  UpdateDeveloperAPITokenPayload,
  WebhookUrlByIdResponse
} from './integrations.model';
import { updateIntuitLoginUrl } from './integrationsSlice';

const IntegrationsBaseApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getIntegrations: builder.query<GetIntegrationsResponse, BaseIntegrationsPayload>({
      query: params => {
        const url = `${API_VERSION.V1}/integrations?teamId=${params.teamId}`;
        return {
          url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Failed to load integrations data',
        showToast: true
      }
    }),
    getDeveloperApiTokens: builder.query<GetDeveloperApiTokensResponse, BaseIntegrationsPayload>({
      query: params => {
        const url = `${API_VERSION.V1}/integrations/tokens?teamId=${params.teamId}`;
        return {
          url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Failed to load tokens data',
        showToast: true
      }
    }),
    createDeveloperApiToken: builder.mutation<CreateDeveloperAPITokenResponse, CreateDeveloperAPITokenPayload>({
      query: params => {
        const url = `${API_VERSION.V1}/integrations/token?teamId=${params.teamId}`;
        return {
          url,
          body: params.request,
          method: 'POST'
        };
      },
      extraOptions: {
        failure: 'Something went wrong, please try again',
        success: 'API key successfully created',
        showToast: true
      }
    }),
    updateDeveloperApiToken: builder.mutation<CreateDeveloperAPITokenResponse, UpdateDeveloperAPITokenPayload>({
      query: params => {
        const url = `${API_VERSION.V1}/integrations/tokens/${params.tokenId}?teamId=${params.teamId}`;
        return {
          url,
          body: params.request,
          method: 'PUT'
        };
      },
      extraOptions: {
        failure: 'Something went wrong, please try again.',
        success: 'New API key successfully created.',
        showToast: true
      }
    }),
    deleteDeveloperApiToken: builder.mutation<string, DeleteDeveloperAPITokenPayload>({
      query: params => {
        const url = `${API_VERSION.V1}/integrations/tokens/${params.tokenId}?teamId=${params.teamId}`;
        return {
          url,
          method: 'DELETE'
        };
      },
      extraOptions: {
        failure: 'Something went wrong, please try again',
        showToast: true
      }
    }),
    getWebhookUrls: builder.query<GetWebhookUrlsResponse, BaseIntegrationsPayload>({
      query: params => {
        const url = `${API_VERSION.V1}/integrations/webhooks?teamId=${params.teamId}`;
        return {
          url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Failed to load webhooks urls data',
        showToast: true
      }
    }),
    getWebhookUrlById: builder.query<WebhookUrlByIdResponse, GetWebhookUrlByIdPayload>({
      query: params => {
        const url = `${API_VERSION.V1}/integrations/webhooks/${params.urlId}?teamId=${params.teamId}`;
        return {
          url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Failed to load webhooks url data',
        showToast: true
      }
    }),
    createWebhookUrl: builder.mutation<WebhookUrlByIdResponse, CreateWebhookUrlPayload>({
      query: params => {
        const url = `${API_VERSION.V1}/integrations/webhook?teamId=${params.teamId}`;
        return {
          url,
          body: params.request,
          method: 'POST'
        };
      },
      extraOptions: {
        failure: 'Something went wrong, please try again',
        success: 'Webhook successfully created',
        showToast: true
      }
    }),
    deleteWebhookUrl: builder.mutation<string, DeleteWebhookUrlPayload>({
      query: params => {
        const url = `${API_VERSION.V1}/integrations/webhooks/${params.urlId}?teamId=${params.teamId}`;
        return {
          url,
          method: 'DELETE'
        };
      },
      extraOptions: {
        failure: 'Something went wrong, please try again',
        showToast: true
      }
    }),
    getQBOLoginUrl: builder.query<QBOLoginURLResponse, BaseIntegrationsPayload>({
      query: params => {
        const url = `/integrations/users/get-quickbooks-url?scanPayTeamId=${params.teamId}`;
        return {
          url,
          method: 'GET'
        };
      },

      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data.url) {
          dispatch(updateIntuitLoginUrl(data.url));
        }
      },
      extraOptions: {
        failure: 'Something went wrong, please try again',
        showToast: true
      }
    }),
    disconnectQBOOnline: builder.query<DisconnectQBOResponse, BaseIntegrationsPayload>({
      query: params => {
        const url = `/integrations/users/disconnect-quickbooks?scanPayTeamId=${params.teamId}`;
        return {
          url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Something went wrong, please try again',
        showToast: true
      }
    }),
    getQBOConnectionStatus: builder.query<QBOConnectionStatusResponse, BaseIntegrationsPayload>({
      query: params => {
        const url = `integrations/users/quickbooks/connection-status?teamId=${params.teamId}`;
        return {
          url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Something went wrong, please try again',
        showToast: true
      }
    }),
    getQBOConnectionData: builder.query<QBOConnectionDataResponse, BaseIntegrationsPayload>({
      query: params => {
        const url = `/integrations/users?teamId=${params.teamId}`;
        return {
          url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Something went wrong, please try again',
        showToast: true
      }
    }),
    editQBOAccount: builder.mutation<any, EditQBOAccountPayload>({
      query: body => {
        const url = '/integrations/users';
        return {
          url,
          method: 'PATCH',
          body
        };
      },
      extraOptions: {
        failure: 'Something went wrong, please try again',
        showToast: true
      }
    }),
    getExpenseAndRevenueAccounts: builder.query<QBOAccountsResponse, GetExpenseAndRevenueAccountsRequest>({
      query: ({ teamId, classificationType }) => {
        const url = `/integrations/users/quickbooks/accounts?teamId=${teamId}&classification=${classificationType}`;
        return {
          url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Something went wrong, please try again',
        showToast: true
      }
    })
  })
});

export const {
  useLazyGetIntegrationsQuery,
  useCreateDeveloperApiTokenMutation,
  useCreateWebhookUrlMutation,
  useDeleteDeveloperApiTokenMutation,
  useDeleteWebhookUrlMutation,
  useLazyGetDeveloperApiTokensQuery,
  useLazyGetWebhookUrlByIdQuery,
  useLazyGetWebhookUrlsQuery,
  useUpdateDeveloperApiTokenMutation,
  useLazyGetQBOLoginUrlQuery,
  useLazyDisconnectQBOOnlineQuery,
  useLazyGetQBOConnectionStatusQuery,
  useLazyGetQBOConnectionDataQuery,
  useEditQBOAccountMutation,
  useLazyGetExpenseAndRevenueAccountsQuery
} = IntegrationsBaseApi;
